import { dispatchAnalyticsEvent } from "@configs/analytics/dispatch";
import { EVENTS } from "@configs/analytics/events/constants";
import type { HomeGameListShownMetadata } from "@configs/analytics/events/types";
import { providersReady } from "@configs/analytics/providers";
import { posthogReady } from "@configs/post-hog/posthog-ready";
import { STORAGE_KEY } from "@constants/misc";
import { Paths } from "@constants/paths";
import { runOnIdle } from "@utils/runOnIdle";

// Default buffer is 250; cold loads make ~300 requests, which would drop the API entries.
const RESOURCE_TIMING_BUFFER_SIZE = 1000;

const state = {
  landedOnHome: false,
  hasCachedSettings: false,
  wasHidden: false,
  posthogReadyMs: null as number | null,
  skeletonShownMs: null as number | null,
  listRenderedMs: null as number | null,
  sent: false,
};

const now = () => Math.round(performance.now());

const findResponseEnd = (entries: PerformanceResourceTiming[], pattern: RegExp): number | null => {
  const entry = entries.find((e) => pattern.test(e.name));
  return entry ? Math.round(entry.responseEnd) : null;
};

/** Runs `callback` after the next paint (two frames); returns a cancel function for effect cleanup. */
export function afterNextPaint(callback: () => void): () => void {
  let secondFrame = 0;
  const firstFrame = requestAnimationFrame(() => {
    secondFrame = requestAnimationFrame(callback);
  });
  return () => {
    cancelAnimationFrame(firstFrame);
    cancelAnimationFrame(secondFrame);
  };
}

/** Must run before React mounts: MemberSettingsLoader writes member-settings soon after. */
export function recordBootState(): void {
  try {
    state.landedOnHome = window.location.pathname === Paths.PRIVATE.HOME;
    if (!state.landedOnHome) return;
    state.hasCachedSettings = localStorage.getItem(STORAGE_KEY.MEMBER_SETTINGS) !== null;
    state.wasHidden = document.visibilityState === "hidden";
    performance.setResourceTimingBufferSize(RESOURCE_TIMING_BUFFER_SIZE);
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "hidden") state.wasHidden = true;
    });
    // Never resolves on brands without PostHog, leaving posthogReadyMs null.
    void posthogReady.then(markPosthogReadyTiming);
  } catch {
    // Timing is best-effort and must never block boot.
  }
}

function markPosthogReadyTiming(): void {
  if (state.posthogReadyMs === null) state.posthogReadyMs = now();
}

export function markSkeletonShown(): void {
  if (state.skeletonShownMs === null) state.skeletonShownMs = now();
}

export function markGameListRendered(): void {
  if (state.listRenderedMs === null) state.listRenderedMs = now();
}

/** Once the user has left "/", any later list on home is an SPA return, not this page load. */
function abandonIfLeftHome(): void {
  if (!state.sent && window.location.pathname !== Paths.PRIVATE.HOME) state.sent = true;
}

/** An empty first category means no list was shown for this page load. */
export function abandonIfNoListYet(): void {
  if (state.listRenderedMs === null) state.sent = true;
}

let listMounted = false;

/** A real unmount before the list shows (game opened, auth gate, route change) ends this page load's measurement. */
export function trackGameListMount(): () => void {
  listMounted = true;
  return () => {
    listMounted = false;
    // StrictMode remounts synchronously, so only a real unmount is still unmounted by the microtask.
    queueMicrotask(() => {
      if (!listMounted) state.sent = true;
    });
  };
}

export function markGameListShown(params: {
  authStatus: HomeGameListShownMetadata["authStatus"];
  category: string;
  fromVault: boolean;
}): void {
  abandonIfLeftHome();
  if (state.sent || !state.landedOnHome || params.fromVault) return;
  state.sent = true;
  // Snapshot now: state keeps changing until the idle callback runs.
  const snapshot = {
    listShownMs: now(),
    listRenderedMs: state.listRenderedMs,
    skeletonShownMs: state.skeletonShownMs,
    posthogReadyMs: state.posthogReadyMs,
    hasCachedSettings: state.hasCachedSettings,
    wasHidden: state.wasHidden,
  };
  // Scan + capture off the post-paint frame so they don't compete with first input on slow phones.
  runOnIdle(() => {
    try {
      const entries = performance.getEntriesByType("resource") as PerformanceResourceTiming[];
      const homeDataEnds = [/\/game\/providers/, /\/category\/list/, /\/game\/stats/].map((pattern) =>
        findResponseEnd(entries, pattern),
      );
      const entryScriptSrc = document.querySelector<HTMLScriptElement>("script[type=module][src]")?.src;
      const entryScript = entries.find((e) => e.name === entryScriptSrc);
      const metadata: HomeGameListShownMetadata = {
        ...snapshot,
        jsCached: entryScript ? entryScript.transferSize === 0 : null,
        infoMs: findResponseEnd(entries, /\/info\?account=/),
        homeDataMs: homeDataEnds.some((end) => end === null) ? null : Math.max(...(homeDataEnds as number[])),
        listApiMs: findResponseEnd(entries, /\/game\/v3\/list\?(?:.*&)?category=/),
        authStatus: params.authStatus,
        category: params.category,
      };
      void providersReady.then(() => dispatchAnalyticsEvent(EVENTS.HOME_GAME_LIST_SHOWN, metadata));
    } catch {
      // Timing is best-effort and must never break the home page.
    }
  });
}
